import { httpClient } from '@/lib/http/fetchWrapper'

// Types based on OpenAPI spec
export type SocialLinkType =
  | 'website'
  | 'telegram'
  | 'twitter'
  | 'github'
  | 'linkedin'
  | 'instagram'
  | 'youtube'
  | 'other'

export interface SocialLink {
  type: SocialLinkType
  url: string
  label?: string | null
}

export interface LinkedProduct {
  id: number
  slug: string
  name: string
  tagline: string
  logoUrl: string
}

export interface UserProfile {
  id: number
  name: string
  username: string
  email?: string | null
  avatarUrl?: string | null
  headline?: string | null
  bio?: string | null
  socialLinks: SocialLink[]
  linkedProduct?: LinkedProduct | null
  profileNeedsReview: boolean
  profileReviewNotes: string | null
  createdAt: string
  updatedAt: string
}

export interface PublicProfile {
  id: number
  name: string
  username: string
  avatarUrl?: string | null
  headline?: string | null
  bio?: string | null
  socialLinks: SocialLink[]
  commentCount: number
  voteCount: number
  productCount: number
}

export interface UpdateProfileRequest {
  name?: string | null
  headline?: string | null
  bio?: string | null
  avatarKey?: string | null  // Upload key from uploadAvatar(), not URL
  socialLinks?: SocialLink[] | null
  linkedProductId?: number | null  // Set to 0 to clear, positive ID to link
}

export interface LinkableProductItem {
  id: number
  name: string
  tagline: string
  logoUrl: string
  role: string
}

export interface LinkableProductsResponse {
  products: LinkableProductItem[]
}

export interface ProfileUpdateResult {
  success: true
  data: UserProfile
}

export interface ProfileError {
  success: false
  error: {
    message: string
    code?: string
  }
}

export type ProfileResult = ProfileUpdateResult | ProfileError

// Get current user's profile
export async function getMyProfile(): Promise<UserProfile> {
  return httpClient.get<UserProfile>('/me')
}

// Get products user can link to profile
export async function getLinkableProducts(): Promise<LinkableProductItem[]> {
  const response = await httpClient.get<LinkableProductsResponse>('/me/linkable-products')
  return response.products
}

// Update current user's profile
export async function updateMyProfile(data: UpdateProfileRequest): Promise<ProfileResult> {
  try {
    // Client-side validation
    if (data.bio && data.bio.length > 500) {
      return {
        success: false,
        error: {
          message: 'Bio must be less than 500 characters',
          code: 'VALIDATION_ERROR'
        }
      }
    }

    if (data.socialLinks && data.socialLinks.length > 10) {
      return {
        success: false,
        error: {
          message: 'Maximum 10 social links allowed',
          code: 'VALIDATION_ERROR'
        }
      }
    }

    if (data.headline && data.headline.length > 40) {
      return {
        success: false,
        error: {
          message: 'Headline must be less than 40 characters',
          code: 'VALIDATION_ERROR'
        }
      }
    }

    if (data.name && data.name.length > 255) {
      return {
        success: false,
        error: {
          message: 'Name must be less than 255 characters',
          code: 'VALIDATION_ERROR'
        }
      }
    }

    const validLinkTypes: SocialLinkType[] = [
      'website', 'telegram', 'twitter', 'github',
      'linkedin', 'instagram', 'youtube', 'other'
    ]

    if (data.socialLinks) {
      for (const link of data.socialLinks) {
        if (!validLinkTypes.includes(link.type)) {
          return {
            success: false,
            error: {
              message: `Invalid social link type: ${link.type}`,
              code: 'VALIDATION_ERROR'
            }
          }
        }
        if (link.label && link.label.length > 50) {
          return {
            success: false,
            error: {
              message: 'Social link label must be less than 50 characters',
              code: 'VALIDATION_ERROR'
            }
          }
        }
      }
    }

    const response = await httpClient.put<UserProfile>('/me', data as unknown as Record<string, unknown>)
    return { success: true, data: response }
  } catch (error) {
    if (error instanceof Error) {
      return {
        success: false,
        error: {
          message: error.message,
          code: 'API_ERROR'
        }
      }
    }
    return {
      success: false,
      error: {
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR'
      }
    }
  }
}

// Delete current user's account
export async function deleteMyAccount(): Promise<ProfileResult> {
  try {
    await httpClient.delete('/me')
    return { success: true, data: {} as UserProfile }
  } catch (error) {
    if (error instanceof Error) {
      return {
        success: false,
        error: {
          message: error.message,
          code: 'API_ERROR'
        }
      }
    }
    return {
      success: false,
      error: {
        message: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR'
      }
    }
  }
}

// ============================================================================
// PUBLIC PROFILE TYPES
// ============================================================================

export interface UserCommentItem {
  id: number
  content: string
  productId: number
  productSlug: string
  productName: string
  productTagline?: string | null
  productLogoUrl: string | null
  parentId: number | null
  depth: number
  voteCount: number
  edited: boolean
  createdAt: string
  updatedAt: string
}

export interface UserCommentsResponse {
  comments: UserCommentItem[]
  total: number
}

export interface UserVotedProductItem {
  id: number
  slug: string
  name: string
  tagline: string
  logoUrl: string
  voteCount: number
  commentCount: number
  categories: { id: number; name: string; slug: string }[]
  createdBy: { id: number; name: string; username: string | null; avatarUrl: string | null }
  votedAt: string
}

export interface UserVotedProductsResponse {
  products: UserVotedProductItem[]
  total: number
}

export interface UserProductItem {
  id: number
  slug: string
  name: string
  tagline: string
  logoUrl: string
  voteCount: number
  commentCount: number
  categories: { id: number; name: string; slug: string }[]
  createdBy: { id: number; name: string; username: string | null; avatarUrl: string | null }
  createdAt: string
}

export interface UserProductsResponse {
  products: UserProductItem[]
  total: number
}

// ============================================================================
// PUBLIC PROFILE API
// ============================================================================

// Get public profile by username
export async function getPublicProfile(username: string): Promise<PublicProfile> {
  return httpClient.get<PublicProfile>(`/u/${username}`)
}

// Get user's comments
export async function getUserComments(username: string): Promise<UserCommentsResponse> {
  return httpClient.get<UserCommentsResponse>(`/u/${username}/comments`, { skipAuth: true })
}

// Get products the user voted for
export async function getUserVotes(username: string): Promise<UserVotedProductsResponse> {
  return httpClient.get<UserVotedProductsResponse>(`/u/${username}/votes`, { skipAuth: true })
}

// Get products created by the user
export async function getUserProducts(username: string): Promise<UserProductsResponse> {
  return httpClient.get<UserProductsResponse>(`/u/${username}/products`, { skipAuth: true })
}

// Social link type labels for UI
export const socialLinkLabels: Record<SocialLinkType, string> = {
  website: 'Website',
  telegram: 'Telegram',
  twitter: 'Twitter/X',
  github: 'GitHub',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
  other: 'Other'
}

// Social link type icons (Lucide icon names)
export const socialLinkIcons: Record<SocialLinkType, string> = {
  website: 'Globe',
  telegram: 'Send',
  twitter: 'Twitter',
  github: 'Github',
  linkedin: 'Linkedin',
  instagram: 'Instagram',
  youtube: 'Youtube',
  other: 'Link'
}
